import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, Briefcase, LifeBuoy, Mail, ArrowRight, ArrowLeft } from "lucide-react";
import SupportForm from "./SupportForm";

const CONTACT_EMAIL = process.env.REACT_APP_CONTACT_EMAIL;
const SUPPORT_EMAIL = process.env.REACT_APP_SUPPORT_EMAIL;

export const openContact = () => window.dispatchEvent(new CustomEvent("vt:open-contact"));

const ContactMenu = ({ onReport }) => (
  <div data-testid="contact-menu" className="grid sm:grid-cols-2 gap-4">
    <div data-testid="contact-commercial-card" className="relative overflow-hidden rounded-2xl border border-brand-red/35 bg-brand-card/80 p-5 flex flex-col gap-3 shadow-[0_0_0_1px_rgba(224,0,0,0.12),0_18px_40px_-24px_rgba(224,0,0,0.6)]">
      <div className="pointer-events-none absolute -right-10 -top-10 w-36 h-36 rounded-full bg-brand-red/20 blur-[60px]" />
      <span className="relative w-11 h-11 rounded-xl bg-brand-red/15 border border-brand-red/50 text-brand-red flex items-center justify-center shadow-[0_0_16px_rgba(224,0,0,0.4)]">
        <Briefcase className="w-5 h-5" strokeWidth={1.75} />
      </span>
      <div className="relative">
        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#FF5A5A]">Contacto comercial</p>
        <p className="text-sm text-brand-text2 mt-1">Para colaboraciones, Enterprise y oportunidades de negocio.</p>
      </div>
      <a href={`mailto:${CONTACT_EMAIL}`} data-testid="contact-commercial-email" className="relative text-sm font-semibold text-white/90 hover:text-white break-all inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-brand-red" />{CONTACT_EMAIL}</a>
      <a href={`mailto:${CONTACT_EMAIL}`} data-testid="contact-commercial-button" className="relative mt-auto inline-flex items-center justify-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-brand-red to-pink-600 shadow-[0_8px_24px_-10px_rgba(224,0,0,0.7)] hover:-translate-y-0.5 hover:shadow-[0_12px_30px_-10px_rgba(224,0,0,0.85)] transition-all duration-300">
        Contactar <ArrowRight className="w-4 h-4" />
      </a>
    </div>

    <div data-testid="contact-support-card" className="relative overflow-hidden rounded-2xl border border-purple-400/35 bg-brand-card/80 p-5 flex flex-col gap-3 shadow-[0_0_0_1px_rgba(139,92,246,0.12),0_18px_40px_-24px_rgba(139,92,246,0.6)]">
      <div className="pointer-events-none absolute -right-10 -top-10 w-36 h-36 rounded-full bg-cyan-500/20 blur-[60px]" />
      <span className="relative w-11 h-11 rounded-xl bg-purple-500/15 border border-purple-400/50 text-cyan-300 flex items-center justify-center shadow-[0_0_16px_rgba(139,92,246,0.4)]">
        <LifeBuoy className="w-5 h-5" strokeWidth={1.75} />
      </span>
      <div className="relative">
        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-purple-300">Soporte</p>
        <p className="text-sm text-brand-text2 mt-1">¿Tienes problemas con tu cuenta, análisis o suscripción?</p>
      </div>
      <a href={`mailto:${SUPPORT_EMAIL}`} data-testid="contact-support-email" className="relative text-sm font-semibold text-white/90 hover:text-white break-all inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-cyan-300" />{SUPPORT_EMAIL}</a>
      <button type="button" onClick={onReport} data-testid="contact-report-button" className="relative mt-auto inline-flex items-center justify-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-purple-600 to-cyan-500 shadow-[0_8px_24px_-10px_rgba(139,92,246,0.7)] hover:-translate-y-0.5 hover:shadow-[0_12px_30px_-10px_rgba(6,182,212,0.7)] transition-all duration-300">
        Reportar un problema <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  </div>
);

export default function ContactModal() {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("menu");

  useEffect(() => {
    const show = () => { setView("menu"); setOpen(true); };
    window.addEventListener("vt:open-contact", show);
    return () => window.removeEventListener("vt:open-contact", show);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (!open) return null;
  const close = () => setOpen(false);

  return createPortal(
    <div data-testid="contact-modal" className="vt-dark-zone fixed inset-0 z-[100] flex items-end sm:items-center justify-center sm:p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={close} />
      <div role="dialog" aria-modal="true" aria-labelledby="contact-modal-title" className="relative w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl border border-white/10 bg-[#0A1024] text-white shadow-2xl p-5 sm:p-8 animate-fadeIn">
        <div className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 w-[420px] h-[220px] rounded-full bg-purple-600/25 blur-[100px]" />
        <button type="button" onClick={close} data-testid="contact-modal-close" aria-label="Cerrar" className="absolute top-4 right-4 p-2 rounded-full bg-white/5 hover:bg-white/15 text-white/70 hover:text-white transition-colors">
          <X className="w-5 h-5" />
        </button>

        <div className="relative mb-6 pr-10">
          {view === "form" && (
            <button type="button" onClick={() => setView("menu")} data-testid="contact-back-button" className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-text2 hover:text-white mb-3 transition-colors">
              <ArrowLeft className="w-3.5 h-3.5" /> Volver
            </button>
          )}
          <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-purple-300">{view === "form" ? "Soporte" : "Contacto"}</p>
          <h3 id="contact-modal-title" data-testid="contact-modal-title" className="text-xl sm:text-2xl font-extrabold mt-1">
            {view === "form" ? "Reportar un problema" : "Estamos aquí para ayudarte."}
          </h3>
        </div>

        <div className="relative">
          {view === "menu" ? <ContactMenu onReport={() => setView("form")} /> : <SupportForm />}
        </div>
      </div>
    </div>,
    document.body
  );
}
